import { useEffect } from 'react'

import { jingleByIdQuery, jingleSubmitted } from '@/entities/tracks'
import {
  DraggerUploadFiles,
  type UploadFileWithId,
} from '@/features/upload-files'
import { confirm } from '@/shared/lib/antd-utils'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin, SelfValidateInput } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { Flex, Form, Modal } from 'antd'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from './lib/constants'
import { getJingleSubmitRequestByFormValues } from './lib/get-jingle-submit-request'
import {
  $isJingleFormModalOpened,
  $jingleFormSubmitting,
  JingleFormGate,
  jingleFormModalClosed,
} from './model/jingle-form.model'

export {
  jingleFormModalOpened,
  jingleFormModalClosed,
} from './model/jingle-form.model'

export const jingleSchema = z.object({
  name: z.string(),
  trackFile: z.custom<UploadFileWithId>().nullable(),
})

export type JingleFormModel = z.infer<typeof jingleSchema>

interface JingleFormProps {
  isModal?: boolean
}
export const JingleForm = ({ isModal }: JingleFormProps) => {
  //   Queries
  const {
    data: jingleData,
    pending: jinglePending,
    error: jingleError,
  } = useUnit(jingleByIdQuery)
  const jingleQueryStatus = useUnit(jingleByIdQuery.$status)

  const submitJingle = useUnit(jingleSubmitted)
  const submitting = useUnit($jingleFormSubmitting)
  //   Modal
  const isModalOpened = useUnit($isJingleFormModalOpened)
  const closeFormModal = useUnit(jingleFormModalClosed)
  //   Form state
  const formik = useFormik<JingleFormModel>({
    enableReinitialize: true,
    initialValues: jingleData
      ? {
          name: jingleData.name,
          trackFile: jingleData.files?.[0]
            ? ({
                id: jingleData.files[0].id,
                uid: jingleData.files[0].id,
                name: jingleData.files[0].name,
                status: 'done',
                url: jingleData.files[0].fullCdnUrl,
              } as UploadFileWithId)
            : null,
        }
      : {
          name: '',
          trackFile: null,
        },
    validationSchema: toFormikValidationSchema(jingleSchema),

    onSubmit: (values: JingleFormModel, { setSubmitting }) => {
      const validate = (): string | null => {
        const errors: string[] = []

        if (!values.name) {
          errors.push('название не указано')
        }
        if (!values.trackFile || values.trackFile.status !== 'done') {
          errors.push('трек не загружен')
        }

        if (errors.length === 0) {
          return null
        }
        const message = errors.join(', ')
        return message[0].toUpperCase() + message.slice(1)
      }
      const alertDescription = validate()
      if (alertDescription) {
        void showNotificationWarningFx({
          message: 'Не все поля заполнены',
          description: alertDescription,
        })
        setSubmitting(false)
        return
      }
      const jingleSubmittedData = getJingleSubmitRequestByFormValues(values)
      submitJingle(jingleSubmittedData)
      setSubmitting(false)
    },
  })

  const { isValid, isValidating, isSubmitting } = formik
  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  // Prevent data lose when try to close

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])
  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    formik.resetForm()
    closeFormModal()
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  const formComponent = (
    <>
      <JingleFormGate
        isModal
        onSubmitted={() => {
          formik.resetForm()
        }}
      />
      <HidingSpin
        spinning={
          jingleQueryStatus !== 'initial' &&
          (jinglePending || (!jingleData && !jingleError))
        }
      >
        <Form.Item layout="vertical" label="Название" required>
          <SelfValidateInput
            showCount
            maxLength={MAX_FIELD_LENGTH.name}
            value={formik.values.name}
            onChange={(value) => {
              void formik.setFieldValue('name', value)
            }}
          />
        </Form.Item>

        <Form.Item label="Загрузка джингла" required layout="vertical">
          <DraggerUploadFiles
            maxCount={1}
            accept=".mp3"
            value={formik.values.trackFile ? [formik.values.trackFile] : null}
            onChange={(fileList) => {
              const file = fileList[0] ?? null
              void formik.setFieldValue('trackFile', file)
            }}
          />
        </Form.Item>

        <Flex justify="flex-end" gap={10}>
          <PrimaryButton
            disabled={formControlButtonsDisabled}
            onClick={() => {
              formik.handleSubmit()
            }}
            loading={submitting}
          >
            {jingleData ? 'Сохранить' : 'Создать'}
          </PrimaryButton>
        </Flex>
      </HidingSpin>
    </>
  )

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            jingleQueryStatus === 'initial'
              ? 'Создание джингла'
              : jinglePending || (!jingleData && !jingleError)
                ? 'Загрузка'
                : 'Редактирование джингла'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
